import { Dispatch, FC, SetStateAction } from 'react';
import { ArrayField, useFormContext } from 'react-hook-form';
import { DsmGrid } from '@dsm-dcs/design-system-react';
import { useIntl } from "../../../../../../_metronic/i18n/customUseIntl";
import { FormType } from "../../common";
import { processAndStageStylesV2 } from "../../../../../../_metronic/layout";
import { UserProfilePrefs } from "../../../../../modules/Helpers/UserProfilePrefs";
import { CSSClassesList } from "../../../../helpers/helperTypes";
import { enumToOptionsArrayWithTranslatedStrings } from "../../../../utils/obj-utils";
import { unitLong } from "../../../../utils/unit-utils";
import RowControlledTextFieldWithMetrics from "../../../../../modules/Farms/Intervention/RowControlledTextFieldWithMetrics2";
import { BeddingType } from "../../../../../../graphql/generated/blonk/cattle";
import {
	DairyV2ContinuousGrowingStageData,
	DairyV2DairyCowStageData,
	DairyV2Intervention,
	DairyV2Stage
} from '../../../../models/Intervention/DairyV2Intervention';
import ReactHookDsmInput from '../../../../../modules/Helpers/ReactHookDsmInput2';
import BeddingInputComponent from '../../CommonDataParts/BeddingInputComponent';
import { Spacer } from '../../../helpers/Spacer';
import { isFieldFilled } from '../CommonDataParts/InterventionGenericInfoDialog';
import { formatLabelName } from '../../../../utils/string-utils';
import EditModeButtons from '../../../helpers/EditModeButtons';

export interface DairyV2InterventionBeddingComponentProps {
	formType: FormType;
	stageIndex: number;
	baseline: DairyV2Stage;
	fieldItemPrefix: string;
	beddingSystemsCollection: {
		isBeddingEdit: boolean;
		setIsBeddingEdit: Dispatch<SetStateAction<boolean>>;
		beddingSystemsAdditions: Partial<ArrayField<Record<string, any>, "keyId">>[];
		appendBeddingSystems: (value: Partial<Record<string, any>> | Partial<Record<string, any>>[], shouldFocus?: boolean) => void;
		removeBeddingSystems: (index?: number | number[]) => void;
		beddingCancelHandler: (isInnerCancel?: boolean) => void;
	}
}

const DairyV2InterventionBeddingComponent: FC<DairyV2InterventionBeddingComponentProps> = ({
	formType = FormType.Add,
	baseline,
	stageIndex = 0,
	fieldItemPrefix,
	beddingSystemsCollection,
}) => {
	const intl = useIntl();
	const userProfile = UserProfilePrefs.getInstance();
	const userUOM = userProfile.getUserUnitPrefs();
	const classes = processAndStageStylesV2() as CSSClassesList;
	const fc = useFormContext<DairyV2Intervention>();
	const {
		isBeddingEdit,
		setIsBeddingEdit,
		beddingSystemsAdditions,
		appendBeddingSystems,
		removeBeddingSystems,
		beddingCancelHandler,
	} = beddingSystemsCollection;
	const barnOutputMassUnit = userUOM?.unitBarnOutputMass
		? unitLong(userUOM.unitBarnOutputMass)
		: "kg";

	const dairyStageData = baseline.stageData as
		| DairyV2ContinuousGrowingStageData
		| DairyV2DairyCowStageData;

	const getBeddingOptions = () => {
		const beddings = enumToOptionsArrayWithTranslatedStrings(
			BeddingType,
			intl,
			"SUSTELL.DAIRY.ENUMS.BEDDING_TYPE"
		);
		const defaultOption = { text: "No bedding", value: "" };
		return [defaultOption].concat(beddings);
	};

	const beefStyles = {
		container: { paddingTop: '0', width: '49%' },
		inputsContainer: { height: '60px' },
	};

	return (
		<>
			{(dairyStageData.housing.beddingSystems || [])
				?.filter((item) => !!item.beddingType)
				?.map((beddingItem, i: number) =>
					<DsmGrid
						key={beddingItem.beddingAmount}
						className={classes.dsmGridOneColumn}
					>
						{
							!isBeddingEdit &&
								!isFieldFilled(fc.getValues(
									`${fieldItemPrefix}.beddingSystems[${i}].beddingAmount`
								)) ? (
								<ReactHookDsmInput
									key={`${fieldItemPrefix}.beddingSystems[${i}].originalBeddingAmount`}
									defaultValue={beddingItem.beddingAmount as string}
									name={`${fieldItemPrefix}.beddingSystems[${i}].originalBeddingAmount`}
									label={formatLabelName(beddingItem.beddingType as string)}
									adornment={barnOutputMassUnit}
									disabled={true}
									type="number"
								/>
							) : (
								<div
									key={`${fieldItemPrefix}.beddingSystems[${i}].beddingType`}
									style={{
										marginTop: i === 0 ? '0px' : '5px',
										marginBottom: '16px',
										...beefStyles.inputsContainer,
									}}>
									<input
										type="hidden"
										value={beddingItem.beddingType}
										ref={fc.register()}
										name={`${fieldItemPrefix}.beddingSystems[${i}].beddingType`}
									/>
									<RowControlledTextFieldWithMetrics
										name={`${fieldItemPrefix}.beddingSystems[${i}].beddingAmount`}
										label={formatLabelName(beddingItem.beddingType as string)}
										tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.BEDDING.AMOUNT.TOOLTIP' })}
										metricUnit={barnOutputMassUnit}
										baseline={beddingItem.beddingAmount}
										disabled={formType === "view" || !isBeddingEdit}
										minAllowedValue={0}
										originalMetricUnit={barnOutputMassUnit}
										displayTypeSelect={isBeddingEdit}
									/>
								</div>
							)
						}
					</DsmGrid>
				)}
			<Spacer />
			{(isBeddingEdit ||
				(!isBeddingEdit && beddingSystemsAdditions?.length !== 0)) && (
					<BeddingInputComponent
						stageIndex={stageIndex}
						beddingOptions={getBeddingOptions()}
						isViewMode={formType === FormType.View || !isBeddingEdit}
						beddingAmoutWeightUnit={barnOutputMassUnit}
						beddingAmountTooltipId="SUSTELL.STAGE.DAIRY.BEDDING.AMOUNT.TOOLTIP"
						isIntervention={true}
						isInterventionEdition={isBeddingEdit}
						fieldsAppendRemoveObject={{
							fields: beddingSystemsAdditions,
							append: appendBeddingSystems,
							remove: removeBeddingSystems,
						}}
						//the isDairyIntervention is to be removed after beef includes this change as well
						isDairyIntervention={true}
					/>
				)}
			<EditModeButtons
				isEditing={isBeddingEdit}
				onEdit={() => setIsBeddingEdit(true)}
				onCancel={beddingCancelHandler}
				disabled={formType === FormType.View}
			/>
		</>
	);
}

export default DairyV2InterventionBeddingComponent;
